import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useShopTimezone } from '@/contexts/TimezoneContext';
import { usePayrollSummary, PayrollSummaryRow, PayrollMode } from '@/hooks/usePayrollSummary';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ArrowLeft, Download, Printer, ChevronDown, HelpCircle } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { PayrollDateRangeFilter } from '@/components/payroll/PayrollDateRangeFilter';
import { PayrollSummaryCards } from '@/components/payroll/PayrollSummaryCards';
import { PayrollTable } from '@/components/payroll/PayrollTable';
import { EmployeeDetailDrawer } from '@/components/payroll/EmployeeDetailDrawer';
import { getPayrollDateRange, getComparisonRange, PayrollDateRange } from '@/utils/payrollDateUtils';
import { exportPayrollCSV, exportQuickBooksIIF, exportQuickBooksOnlineCSV, PayrollExportRow } from '@/utils/payrollExport';
import { TopBar } from '@/components/layout/TopBar';

type EmployeeTypeFilter = 'all' | 'technicians' | 'staff';

export default function PayrollReports() {
  const navigate = useNavigate();
  const { activeShop } = useAuth();
  const { shopTimezone } = useShopTimezone();

  // Date range state
  const [dateRange, setDateRange] = useState<PayrollDateRange>(() => 
    getPayrollDateRange('wtd', shopTimezone)
  );
  const [compareToLY, setCompareToLY] = useState(false);

  // Filter state
  const [employeeType, setEmployeeType] = useState<EmployeeTypeFilter>('all');
  const [producedMode, setProducedMode] = useState<PayrollMode>('booked');
  const [includeZeros, setIncludeZeros] = useState(false);

  // Detail drawer state
  const [selectedEmployee, setSelectedEmployee] = useState<PayrollSummaryRow | null>(null);

  // Fetch payroll data
  const { data: payrollData = [], isLoading } = usePayrollSummary({
    shopId: activeShop?.id,
    startDate: dateRange.startDate,
    endDate: dateRange.endDate,
    mode: producedMode,
    includeZeros,
  });

  // Fetch comparison data if enabled
  const comparisonRange = compareToLY ? getComparisonRange(dateRange) : null;
  const { data: compareData = [] } = usePayrollSummary({
    shopId: activeShop?.id,
    startDate: comparisonRange?.startDate || new Date(),
    endDate: comparisonRange?.endDate || new Date(),
    mode: producedMode,
    includeZeros,
  });

  // Filter data by employee type
  const filteredData = payrollData.filter(row => {
    if (employeeType === 'technicians') return row.employee_type === 'technician';
    if (employeeType === 'staff') return row.employee_type === 'staff';
    return true;
  });

  const filteredCompareData = compareToLY ? compareData.filter(row => {
    if (employeeType === 'technicians') return row.employee_type === 'technician';
    if (employeeType === 'staff') return row.employee_type === 'staff';
    return true;
  }) : undefined;

  // Handle date range change
  const handleRangeChange = (range: PayrollDateRange, compareLY: boolean) => {
    setDateRange(range);
    setCompareToLY(compareLY);
  };

  // Export handlers
  const handleExport = (format: 'csv' | 'iif' | 'qbo') => {
    const exportData: PayrollExportRow[] = filteredData.map(row => ({
      employeeName: row.employee_name,
      employeeType: row.employee_type,
      roleLabel: row.role_label,
      clockMinutes: row.clock_minutes,
      afterHoursMinutes: row.after_hours_minutes,
      producedMinutes: row.produced_minutes,
      efficiency: row.employee_type === 'technician' && row.clock_minutes > 0
        ? Math.round((row.produced_minutes / row.clock_minutes) * 100)
        : null,
    }));

    switch (format) {
      case 'csv':
        exportPayrollCSV(exportData, dateRange);
        break;
      case 'iif':
        exportQuickBooksIIF(exportData, dateRange);
        break;
      case 'qbo':
        exportQuickBooksOnlineCSV(exportData, dateRange);
        break;
    }
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <TopBar />
      <div className="flex-1 space-y-6 p-6">
      {/* Print Header - only visible when printing */}
      <div className="print-header hidden print:block mb-4">
        <h1 className="text-xl font-bold">{activeShop?.name} - Payroll Report</h1>
        <p className="text-sm text-muted-foreground">
          {dateRange.label} | Generated {new Date().toLocaleString()}
        </p>
      </div>

      {/* Header */}
      <div className="flex justify-between items-center no-print">
        <div>
          <h1 className="text-2xl font-bold">Payroll & Productivity</h1>
          <p className="text-muted-foreground mt-1">Track clock hours and technician productivity</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/shop-profile?tab=admin')} variant="outline" size="sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Button onClick={handlePrint} variant="outline" size="sm">
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
                <ChevronDown className="h-4 w-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                Export CSV (Generic)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('iif')} className="flex items-center gap-2">
                QuickBooks Desktop (IIF – TIMEACT)
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-3 w-3 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Employee names must match your QuickBooks Desktop employee list exactly.
                  </TooltipContent>
                </Tooltip>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('qbo')} className="flex items-center gap-2">
                QuickBooks Online (Timesheet CSV)
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-3 w-3 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Verify column headers match your QB Online import requirements.
                  </TooltipContent>
                </Tooltip>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Filters */}
      <Card className="no-print">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-end gap-6">
            <PayrollDateRangeFilter
              timezone={shopTimezone}
              onRangeChange={handleRangeChange}
              showCompareToggle={true}
            />

            <div className="space-y-1.5">
              <Label className="text-sm">Employee Type</Label>
              <Select value={employeeType} onValueChange={(v: EmployeeTypeFilter) => setEmployeeType(v)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Staff</SelectItem>
                  <SelectItem value="technicians">Technicians</SelectItem>
                  <SelectItem value="staff">Non-Tech Staff</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label className="text-sm">Produced Hours Mode</Label>
              <Select value={producedMode} onValueChange={(v: PayrollMode) => setProducedMode(v)}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="booked">Approved labor</SelectItem>
                  <SelectItem value="wrench">Actual labor (timers)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="include-zeros"
                checked={includeZeros}
                onCheckedChange={setIncludeZeros}
              />
              <Label htmlFor="include-zeros" className="text-sm cursor-pointer">
                Show Zero Activity
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <PayrollSummaryCards 
        data={filteredData} 
        loading={isLoading}
        compareData={filteredCompareData}
      />

      {/* Main Table */}
      <PayrollTable
        data={filteredData}
        loading={isLoading}
        onRowClick={setSelectedEmployee}
        compareData={filteredCompareData}
      />

      {/* Employee Detail Drawer */}
      <EmployeeDetailDrawer
        open={!!selectedEmployee}
        onClose={() => setSelectedEmployee(null)}
        employee={selectedEmployee}
        shopId={activeShop?.id}
        startDate={dateRange.startDate}
        endDate={dateRange.endDate}
      />
      </div>
    </div>
  );
}
